import React from 'react'
import { useNavigate } from 'react-router-dom'
import { X, MailWarning } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import type { NotificationRecord } from '../services/notificationsApi'

interface CampaignPausedAlertRowProps {
  notification: NotificationRecord
  onDismiss: (id: string) => void
  onAfterReconnect?: () => void
}

// A CAMPAIGN_SEND_PAUSED alert (ZAI-387) — the rep's Gmail connection broke,
// so every active outreach sequence they own is stalling. Deliberately no
// auto-dismiss variant (unlike QualificationResultCard): this stays in the
// panel until the rep explicitly clears it, even after they reconnect.
export const CampaignPausedAlertRow: React.FC<CampaignPausedAlertRowProps> = ({ notification, onDismiss, onAfterReconnect }) => {
  const { currentTheme: t } = useTheme()
  const navigate = useNavigate()

  return (
    <div
      className="rounded-2xl p-4 flex flex-col gap-3"
      style={{ backgroundColor: t.sectionBackground, border: `1px solid ${t.border}` }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[13.5px] font-semibold truncate" style={{ color: t.text }}>Outreach paused</p>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold shrink-0"
              style={{ backgroundColor: t.errorBackground, color: t.errorText }}
            >
              Gmail disconnected
            </span>
          </div>
          <p className="text-[12px] mt-0.5" style={{ color: t.mutedText }}>
            Your Gmail connection needs reconnecting. Active sequences are paused until you reconnect.
          </p>
        </div>
        <button
          type="button"
          onClick={() => onDismiss(notification.id)}
          aria-label="Dismiss"
          className="p-1 rounded-md shrink-0"
          style={{ color: t.mutedText }}
        >
          <X size={14} />
        </button>
      </div>

      <button
        type="button"
        onClick={() => {
          navigate('/dashboard/settings?tab=integrations')
          onAfterReconnect?.()
        }}
        className="flex items-center justify-center gap-1.5 h-8 rounded-lg text-[12px] font-semibold text-white"
        style={{ background: t.accentGradient }}
      >
        <MailWarning size={12} />
        Reconnect Gmail
      </button>
    </div>
  )
}
